import { getCookie } from 'utils/helpers';
import { handle401Error, handle403Error } from 'utils/authErrorHandler';
import { FeedbackEntry, FeedbackThreadResponse } from './types';

// Same env var/URL convention as this feature's own slice/saga.ts
// (`REACT_APP_API_V2_URL` already resolves to `.../product-roadmap/api/v2` -
// only `/approvals/...` needs appending), kept here rather than importing
// from saga.ts since this fetch runs outside the redux-saga pipeline (see
// FeedbackSlideOut.tsx's own comment on why this feature's feedback-thread
// tab uses a plain useEffect fetch instead).
const API_V2_URL = process.env.REACT_APP_API_V2_URL;

// Same auth-header convention momentsApi.ts/featuresApi.ts already use for
// their own plain-fetch (non-saga) endpoints.
function getAuthHeaders(): Record<string, string> {
  const token = getCookie('app_access_token');
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
}

export interface FeedbackThreadApiResult {
  success: boolean;
  data?: FeedbackThreadResponse;
  message?: string;
}

export interface FetchFeedbackThreadParams {
  /** Release Moment/RSD's own issueId (ARM-...) - see types.ts's Feedback
   * slide-out comment for why this, not the approval request's issueId, is
   * the path param. */
  issueId: string;
  milestoneId: string;
  milestoneName: string;
  requestId?: string;
}

/**
 * `GET /api/v2/approvals/{issueId}/{milestoneId}/feedback-thread
 * ?milestoneName={name}&requestId={requestId}` (requestId is optional) -
 * mirrors the same plain-fetch + status-code-switch
 * + `{ success, data?, message? }` shape momentsApi.ts's fetchChangeLog /
 * featuresApi.ts's fetchFeaturesByIssue already use for this app's other
 * slide-out data fetches.
 */
export async function fetchFeedbackThread({
  issueId,
  milestoneId,
  milestoneName,
  requestId
}: FetchFeedbackThreadParams): Promise<FeedbackThreadApiResult> {
  try {
    const query = new URLSearchParams({ milestoneName });
    if (requestId?.trim()) query.set('requestId', requestId);
    const url = `${API_V2_URL}/approvals/${encodeURIComponent(
      issueId
    )}/${encodeURIComponent(milestoneId)}/feedback-thread?${query}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
      credentials: 'include'
    });

    if (!response.ok) {
      switch (response.status) {
        case 404:
          return {
            success: false,
            message: 'No feedback thread found for this milestone'
          };
        case 401:
          handle401Error('Fetching feedback thread');
          return { success: false, message: 'Authentication required' };
        case 403:
          handle403Error('Fetching feedback thread');
          return {
            success: false,
            message: 'You do not have permission to view this feedback'
          };
        case 500:
          return {
            success: false,
            message: 'Internal server error. Please try again later.'
          };
        default:
          return {
            success: false,
            message: `Feedback thread request failed with status ${response.status}`
          };
      }
    }

    const data: FeedbackThreadResponse = await response.json();
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      message:
        error instanceof Error
          ? error.message
          : 'Network error. Check your connection and try again.'
    };
  }
}

/**
 * Extracts a human-readable message from an error response body, the same
 * best-effort `errorData.message || errorData.error` shape RsdForms/api.ts's
 * `updateMoment` already reads from a failed PATCH.
 */
async function readErrorMessage(
  response: Response,
  fallback: string
): Promise<string> {
  try {
    const errorData = await response.json();
    return errorData?.message || errorData?.error || fallback;
  } catch {
    return fallback;
  }
}

export interface FeedbackThreadEntryPatchResponse {
  content: string;
  editedAt?: string | null;
  edited?: boolean;
}

/**
 * Thrown instead of a plain `Error` for a 401/403 PATCH failure -
 * `handle401Error`/`handle403Error` (utils/authErrorHandler.ts) already show
 * their own centralized toast for these two statuses (that's their whole
 * purpose - see that file's own doc comment), so a caller catching this must
 * NOT also show its own generic failure toast for it, or the user sees two
 * toasts for one failure. Callers should still run their other failure
 * handling (revert optimistic state, etc.) - only the "show an error toast"
 * step is skipped when `error instanceof AlreadyNotifiedError`.
 */
export class AlreadyNotifiedError extends Error {}

/**
 * `PATCH /api/v2/approvals/feedback-thread/{feedbackId}` - edits an existing
 * Requestor Note or Feedback entry's content. Throws on failure rather than
 * returning a `{success, ...}` wrapper, the same convention RsdForms/api.ts's
 * `updateMoment` uses for the RSD Overview's own rich-text field saves (the
 * pattern this feature's Edit/Save flow reuses - see
 * FeedbackThreadEntryCard.tsx) - callers wrap this in try/catch and revert
 * their own optimistic state on rejection, exactly as Overview.tsx already
 * does around `updateMoment`.
 *
 * `entryId` is deliberately NOT always `feedbackId` from the response shape:
 * the endpoint's path segment is named `feedbackId`, but a Requestor Note
 * entry has no `feedbackId` field at all (only its own `requestorNoteId`) -
 * callers must pass whichever id the entry they're editing actually has
 * (`RequestorNoteEntry.requestorNoteId` for a note, `FeedbackEntry.feedbackId`
 * for a feedback reply). Do not assume the two id fields are interchangeable.
 */
export async function patchFeedbackThreadEntry(
  entryId: string,
  content: string
): Promise<FeedbackThreadEntryPatchResponse> {
  const url = `${API_V2_URL}/approvals/feedback-thread/${encodeURIComponent(
    entryId
  )}`;

  const response = await fetch(url, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    credentials: 'include',
    body: JSON.stringify({ content })
  });

  if (!response.ok) {
    if (response.status === 401) {
      handle401Error('Updating feedback thread entry');
      throw new AlreadyNotifiedError('Authentication required');
    }
    if (response.status === 403) {
      handle403Error('Updating feedback thread entry');
      throw new AlreadyNotifiedError(
        'You do not have permission to edit this entry'
      );
    }
    const message = await readErrorMessage(
      response,
      `Failed to update entry: ${response.status}`
    );
    throw new Error(message);
  }

  return response.json();
}

/**
 * `POST /api/v2/approvals/{issueId}/{milestoneId}/feedback-thread
 * ?milestoneName={name}` - the same path the GET above reads from. Throws on
 * failure (see `patchFeedbackThreadEntry`'s own comment on why - mirrors
 * `updateMoment`). Resolves with the newly created `FeedbackEntry` exactly as
 * the API returns it (201 Created) - the caller (NewFeedbackEditor.tsx via
 * FeedbackSlideOut.tsx) appends this to `thread.entries` as FEEDBACK; it does
 * not construct a local entry from the typed content/current user.
 */
export async function postFeedbackThreadEntry(
  { issueId, milestoneId, milestoneName }: FetchFeedbackThreadParams,
  content: string
): Promise<Omit<FeedbackEntry, 'type'>> {
  const query = new URLSearchParams({ milestoneName }).toString();
  const url = `${API_V2_URL}/approvals/${encodeURIComponent(
    issueId
  )}/${encodeURIComponent(milestoneId)}/feedback-thread?${query}`;

  const response = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    credentials: 'include',
    body: JSON.stringify({ content })
  });

  if (!response.ok) {
    if (response.status === 401) {
      handle401Error('Sending feedback');
      throw new Error('Authentication required');
    }
    if (response.status === 403) {
      handle403Error('Sending feedback');
      throw new Error('You do not have permission to send feedback');
    }
    const message = await readErrorMessage(
      response,
      `Failed to send feedback: ${response.status}`
    );
    throw new Error(message);
  }

  return response.json();
}
